import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import * as React from 'react';

import { useBills } from '@/Billing/useBills';
import { Structure } from '@/Structures/types';
import { BillHistoryTable } from '@/Units/billHistory';

type Props = {
  tenant: { id: string; name: string; email: string };
  structure: Structure;
  onClose: () => void;
};

/**
 * A tenant's billing history, opened from the Tenants table row menu. Same
 * mechanism as the unit page's Bills tab: the property's bills are fetched once
 * (SWR-deduped, shared with every other bills consumer) and each bill's invoices
 * are filtered client-side, here by the tenant the invoice was issued to.
 */
export const TenantBillHistoryDialog = ({ tenant, structure, onClose }: Props): JSX.Element => {
  const { bills, isLoading } = useBills(structure.id);

  // Bills where this tenant has no invoice are dropped entirely rather than kept
  // as empty shells, so a tenant with zero invoices hits the table's NoRows
  // empty state instead of rendering an empty body.
  const tenantBills = React.useMemo(
    () =>
      (bills || [])
        .map((bill) => ({
          ...bill,
          invoices: bill.invoices.filter((invoice) => invoice.subscriberId === tenant.id),
        }))
        .filter((bill) => bill.invoices.length > 0),
    [bills, tenant.id],
  );

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
      >
        <span>
          Bills for {tenant.name || tenant.email}
          {tenant.name && (
            <Typography variant="body2" color="text.secondary">
              {tenant.email}
            </Typography>
          )}
        </span>
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <LinearProgress sx={{ my: 4 }} />
        ) : (
          <>
            <BillHistoryTable data={tenantBills} timeZone={structure.timezone_key} />
            {tenantBills.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                No bills found for this tenant. Invoices generated before this tenant was linked to
                their unit may not appear here.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
